import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import { darken, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { TicketSource } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';
import { Ticket } from '~hooks/useTickets/models';
import DuplicateTicketIndicator from '~pages/Approvals/ApprovalsComponents/DuplicateTicketIndicator';
import { TicketExportIndicator } from '~pages/Approvals/ApprovalsComponents/TicketExport';
import TicketSourceBadge from '~pages/Dispatch/components/tickets-table/TicketSourceBadge';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

const IMAGE_SIZE_IN_PX = 50;

interface TicketColumnProps {
  job: Job;
  onPotentialDuplicateChange?: (potentialDuplicate: boolean) => Promise<void>;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function TicketColumn({
  job,
  onPotentialDuplicateChange,
  sx,
  ticket,
}: TicketColumnProps) {
  const imagePreviewUrl = ticket?.previewUrl;
  const imageUrl = ticket?.imageUrl;
  const ticketNumber = ticket?.ticketNumber;
  const hasNeitherImageNorTicketNumber = !imagePreviewUrl && !ticketNumber;

  const { userStore } = useStores();
  const isReadOnlyTicket = isViewerAccountCustomer(userStore, job.order?.account);
  const userPermissions = userStore.getPermissions();
  const canCreateTicket = !isReadOnlyTicket && userPermissions.canCreateTicket;

  return (
    <Box sx={{ ...sx, overflow: 'hidden' }}>
      {ticket ? (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
          {imagePreviewUrl && (
            <>
              <AttachmentImage url={imagePreviewUrl} ticketSource={ticket.source} />
              <Box sx={{ overflow: 'hidden' }}>
                <PseudoLink
                  title={ticketNumber || t('approvals.ticket_details.view_image')}
                  action={(event?: React.MouseEvent) => {
                    event?.stopPropagation();
                    window.open(imageUrl, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                />
                <DuplicateTicketIndicator
                  onPotentialDuplicateChange={onPotentialDuplicateChange}
                  ticket={ticket}
                />
                <TicketExportIndicator ticket={ticket} />
              </Box>
            </>
          )}

          {ticketNumber && !imagePreviewUrl && (
            <Box sx={{ overflow: 'hidden' }}>
              <OverflowAwareText>{ticketNumber}</OverflowAwareText>
              <DuplicateTicketIndicator
                onPotentialDuplicateChange={onPotentialDuplicateChange}
                ticket={ticket}
              />
              <TicketExportIndicator ticket={ticket} />
            </Box>
          )}

          {hasNeitherImageNorTicketNumber && <TicketExportIndicator ticket={ticket} />}
        </Box>
      ) : canCreateTicket ? (
        <Typography
          sx={(theme) => ({
            alignItems: 'center',
            color: theme.brandV2.colors.treadOrangeDark,
            display: 'flex',
            fontSize: '12px',
            gap: 0.5,
            '&:hover': { color: darken(theme.brandV2.colors.treadOrangeDark, 0.2) },
          })}
        >
          <AddCircleOutline sx={{ fontSize: '20px' }} />

          <Typography component="span" sx={{ fontSize: 'inherit', mt: '2px' }}>
            {t('approvals.files.loads_table.ctas.add_ticket')}
          </Typography>
        </Typography>
      ) : null}
    </Box>
  );
}

interface AttachmentImageProps {
  url: string;
  ticketSource: Nullable<TicketSource>;
}

function AttachmentImage({ url, ticketSource }: AttachmentImageProps) {
  return (
    <Box
      sx={(theme) => ({
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 1,
        display: 'flex',
        flex: 0,
        height: `${IMAGE_SIZE_IN_PX}px`,
        minWidth: `${IMAGE_SIZE_IN_PX}px`,
        overflow: 'hidden',
        position: 'relative',
      })}
    >
      {ticketSource && <TicketSourceBadge ticketSource={ticketSource} />}
      <Box
        component="img"
        alt="ticket-preview"
        crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
        src={url}
        sx={{
          display: 'block',
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
        }}
      />
    </Box>
  );
}
